import React, { type ReactNode, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ContentLoadingTemplate from '@/Components/Templates/ContentLoadingTemplate';
import App from '@/Components/App';
import { useAuthStore } from '@/Stores/AuthStore';
import {parseTokenPermissions} from "@/Util/parseTokenPermissions";
import {useGetAuthUser} from "@/Service/Api/V1/ApiHooks/Auth/useGetAuthUser";
import {url} from "@/Helpers/general";

interface AppInitWrapperProps {
    children?: ReactNode
}

const AppInitWrapper: React.FC<AppInitWrapperProps> = ({}: AppInitWrapperProps) => {
    const {
        user,
        isAuthenticated,
        isLoading,
        getAccessTokenSilently,
        loginWithRedirect
    } = useAuth0();

    const auth0User = useAuthStore((state) => state.auth0User);
    const accessToken = useAuthStore((state) => state.accessToken);

    const setAuth0User = useAuthStore((state) => state.setAuth0User);
    const setAccessToken = useAuthStore((state) => state.setAccessToken);
    const setImpersonatedOrgId = useAuthStore((state) => state.setImpersonatedOrgId);

    const storedAuthUser = useAuthStore((state) => state.authUser);
    const setAuthUser = useAuthStore((state) => state.setAuthUser);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            getAccessTokenSilently()
                .then((token) => {
                    localStorage.setItem('auth_token', token);
                    setAccessToken(token);
                    const permissions = parseTokenPermissions(token);
                    setAuth0User({...user, permissions});
                })
                .catch(() => {
                    loginWithRedirect();
                });
        }
    }, [isLoading, isAuthenticated, setAuth0User, getAccessTokenSilently, user, setAccessToken, loginWithRedirect]);

    const auth0UserInfoLoading = useMemo(() => {
        return isLoading ||
            (
                (!isLoading && isAuthenticated) &&
                (!auth0User || !accessToken)
            );
    }, [isLoading, isAuthenticated, auth0User, accessToken]);

    const {
        data: authUser,
        isLoading: authUserIsLoading,
        isSuccess: authUserIsSuccess,
        isError: authUserIsError,
        error: authUserError
    } = useGetAuthUser({ enabled: !auth0UserInfoLoading, retry: false });

    const authUserInfoLoading = useMemo(() => {
        return authUserIsLoading ||
            (
                (!authUserIsLoading && authUserIsSuccess) &&
                (!storedAuthUser)
            );
    }, [authUserIsLoading, authUserIsSuccess, storedAuthUser]);

    useEffect(() => {
        if (!authUserIsLoading && authUserIsSuccess) {
            setAuthUser(authUser);
        }
        if (
            !auth0UserInfoLoading
            && isAuthenticated
            && authUserIsError
            && authUserError
        ) {
            if (
                authUserError.response.status === 401
                &&window.location.pathname != url('org-unavailable')
            ) {
                window.location.replace(url('org-unavailable'));
            }
        }
    }, [authUser, authUserIsLoading, authUserIsSuccess, setAuthUser, setImpersonatedOrgId]);

    const initialDataIsLoading = useMemo(() => {
        return auth0UserInfoLoading ||
            authUserInfoLoading;
    }, [auth0UserInfoLoading, authUserInfoLoading]);

    if (initialDataIsLoading) {
        return <ContentLoadingTemplate/>;
    }
    return <App/>;
};

export default AppInitWrapper;
